import styled from 'styled-components';
import { starfishCssUrl } from '@/app/common/icons/starfish';

/**
 * Figma's Alert-DS (5193:56400, identical in the mobile frame 5193:60715) is two boxes: the outer
 * one centres its content in a 56px-tall band, the inner one lays the icon beside the text and
 * pins it to the *top* of the text block. Both carry 8px of padding, so the content sits 16px in.
 * The two levels are kept here because they are what makes a one-line banner read centred and a
 * two-line one keep its icon on the first line.
 */
const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.kallisto.space.space0};
  min-height: 56px;
  padding: ${(p) => p.theme.kallisto.space.space100};
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderWarningSubtle};
  border-radius: ${(p) => p.theme.kallisto.border.radius.radius100};
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundWarningSubtle};
`;

const Content = styled.div`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  align-items: flex-start;
  gap: ${(p) => p.theme.kallisto.space.space100};
  padding: ${(p) => p.theme.kallisto.space.space100};
`;

/* The glyph is drawn with fill="currentColor", which an <img> cannot inherit, so it is masked in
   and painted with the warning colour. Held at Figma's 20px so the flex row cannot stretch it. The
   mask is drawn at 24px so the circle still fills the box edge to edge, as the old one did: only the
   Starfish grid's padding falls outside it. */
const Icon = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background-color: ${(p) => p.theme.kallisto.color.icon.static.iconWarning};
  mask-image: url('${starfishCssUrl('Semantics', 'icon-circle-solid-warning')}');
  mask-size: 24px 24px;
  mask-repeat: no-repeat;
  mask-position: center;
`;

/**
 * Bullets appear only once there is more than one message to tell apart — a lone warning is plain
 * text in Figma 5193:60176. `outside` keeps the marker out of the text column, so a message that
 * wraps stays aligned under its own first word (5193:60715, the narrowest frame).
 */
const Messages = styled.ul<{ $showBullets: boolean }>`
  margin: 0;
  padding-left: 0;
  list-style: ${(p) => (p.$showBullets ? 'disc' : 'none')} outside;
  ${(p) => p.theme.kallisto.mixins.font('BodySmall')}
  color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};

  li {
    /* Figma indents the text 21px and lets the marker sit in that gutter. */
    margin-inline-start: ${(p) => (p.$showBullets ? '21px' : '0')};
  }

  li + li {
    margin-top: ${(p) => p.theme.kallisto.space.space50};
  }
`;

export type EvoLegWarningBannerProps = {
  /** Already-translated warnings for this leg, in the order Figma lists them. */
  messages: string[];
};

/**
 * MPH-958: the amber banner at the top of a leg card, carrying every warning that leg has at once —
 * a different airport at either of its ends, and an arrival on another date.
 */
export function EvoLegWarningBanner({ messages }: EvoLegWarningBannerProps) {
  if (messages.length === 0) return null;

  return (
    <Banner>
      <Content>
        <Icon aria-hidden="true" />
        <Messages $showBullets={messages.length > 1}>
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </Messages>
      </Content>
    </Banner>
  );
}
